import { useMemo } from 'react';
import { useSession } from '@/stores/session';
import { useApi } from './kit/hooks';
import type { Role, UserRow, UsersPayload } from './types';

/**
 * 账号 + 角色：
 * - 角色走 GET /api/roles（roles.read），权限码已由服务端规范；
 * - 账号走 GET /api/users（users.read）；它顺带返回 rolePermissions（有 roles.read / users.create / users.edit 时才有）。
 * 账号上的 roleDefinition.permissions 是库里原样（可能是中文句子），一律按 roleCode 去角色表里取。
 */
export function useAccountsAndRoles() {
  const can = useSession((st) => st.can);
  const canRoles = can('roles.read');
  const canUsers = can('users.read');
  const roles = useApi<Role[]>(canRoles ? '/api/roles' : null);
  const users = useApi<UsersPayload>(canUsers ? '/api/users' : null);
  const roleList = useMemo(() => roles.data || users.data?.rolePermissions || [], [roles.data, users.data]);
  const roleByCode = useMemo(() => new Map(roleList.map((r) => [r.code, r])), [roleList]);
  const userList: UserRow[] = users.data?.users || [];
  const roleOf = (u: UserRow) => roleByCode.get(u.roleCode || u.role);
  const membersOf = (r: Role) => userList.filter((u) => (u.roleCode || u.role) === r.code);
  return {
    canRoles,
    canUsers,
    roles: roleList,
    users: userList,
    roleOf,
    membersOf,
    rolesState: roles,
    usersState: users,
  };
}
